import { useState } from "react";
import { Sparkles, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import { API_URL, getToken } from "@/lib/api";

// GPT occasionally slips markdown symbols in despite the plain-text system
// prompt — strip them cosmetically before display so the analysis reads as
// clean prose in this plain-text panel.
const clean = (t) => t.replace(/\*\*/g, "").replace(/^#+\s*/gm, "").replace(/^[-*]\s+/gm, "");

export default function AiDiagnostic({ deviceId, initial }) {
  const [text, setText] = useState(initial || "");
  const [streaming, setStreaming] = useState(false);

  const run = async () => {
    setStreaming(true); setText("");
    try {
      const res = await fetch(`${API_URL}/devices/${deviceId}/sizing/explain`, { method: "POST", headers: { Authorization: `Bearer ${getToken()}` }, credentials: "include" });
      if (!res.ok) throw new Error(`Analysis failed (${res.status})`);
      const reader = res.body.getReader();
      const dec = new TextDecoder();
      let buf = "";
      while (true) {
        const { value, done } = await reader.read();
        if (done) break;
        buf += dec.decode(value, { stream: true });
        const lines = buf.split("\n\n");
        buf = lines.pop();
        for (const l of lines) {
          if (!l.startsWith("data: ")) continue;
          const payload = l.slice(6);
          if (payload === "[DONE]") continue;
          const j = JSON.parse(payload);
          if (j.error) throw new Error(j.error);
          setText((t) => t + j.t);
        }
      }
    } catch (e) { toast.error(e.message); } finally { setStreaming(false); }
  };

  return (
    <div className="kl-panel-dark p-6" data-testid="ai-diagnostic">
      <p className="kl-label-dark">Diagnostic output</p>
      <h3 className="font-display font-light text-2xl mt-1">Engineer's analysis</h3>

      <div className="mt-5 border border-kl-line bg-kl-surface p-4 h-[220px] overflow-y-auto kl-scroll font-body text-sm leading-relaxed text-white/90 whitespace-pre-wrap" data-testid="ai-diagnostic-text">
        {text ? <span className={streaming ? "kl-caret" : ""}>{clean(text)}</span> : streaming ? <span className="kl-caret font-mono text-xs text-kl-muted">&gt; querying sizing model </span> : <span className="font-mono text-xs text-kl-muted">&gt; idle. Run the analysis to narrate this client's load pattern and why the recommended array fits.</span>}
      </div>

      <div className="flex items-center justify-between mt-4">
        <p className="text-[10px] font-mono text-kl-muted">inputs: measured telemetry + sizing engine output</p>
        <button onClick={run} disabled={streaming} data-testid="run-analysis-button" className={text ? "kl-btn-outline-dark h-9 px-3 text-xs shrink-0" : "kl-btn-primary h-9 px-3 text-xs shrink-0"}>
          {text ? <RefreshCw size={13} className={streaming ? "animate-spin" : ""} /> : <Sparkles size={13} />}
          {streaming ? "Analysing…" : text ? "Re-run" : "Run analysis"}
        </button>
      </div>
    </div>
  );
}
